import { ROUTES } from "@src/common/constants";
import {
	parseCardDocumentId,
	parseWhiteboardDocumentId,
} from "@src/components/collaboration/document-id";
import type { AppAgentNavigateAppInput } from "./hooks/app-agent-context";

export type ResolvedAppNavigatePath =
	| {
			pathname: string;
			whiteboardId?: string;
			cardId?: string;
	  }
	| { error: string };

/**
 * Resolve an agent `navigateApp` input to an authenticated SPA pathname.
 * Accepts typed document ids (`Whiteboard-…` / `Card-…`) or raw aggregate
 * ids; route segments always use the raw id (`toIdString()`).
 */
export function resolveAppNavigatePath(
	input: AppAgentNavigateAppInput,
): ResolvedAppNavigatePath {
	switch (input.destination) {
		case "ask":
			return { pathname: ROUTES.ask };
		case "library":
			return { pathname: ROUTES.library };
		case "whiteboards":
			return { pathname: ROUTES.whiteboards };
		case "trash":
			return { pathname: ROUTES.trash };
		case "draft":
			return { pathname: ROUTES.draft };
		case "profile":
			return { pathname: ROUTES.profile };
		case "whiteboard": {
			const raw = input.whiteboardId?.trim();
			if (!raw) {
				return {
					error: "whiteboardId is required when destination is whiteboard",
				};
			}
			if (raw.includes("/")) {
				return { error: "whiteboardId must be a single path segment" };
			}
			let whiteboardId: string;
			try {
				whiteboardId = parseWhiteboardDocumentId(raw).toIdString();
			} catch {
				return { error: "whiteboardId is invalid" };
			}
			if (whiteboardId.includes("/")) {
				return { error: "whiteboardId must be a single path segment" };
			}
			return {
				pathname: `${ROUTES.whiteboards}/${whiteboardId}`,
				whiteboardId,
			};
		}
		case "card": {
			const raw = input.cardId?.trim();
			if (!raw) {
				return { error: "cardId is required when destination is card" };
			}
			if (raw.includes("/")) {
				return { error: "cardId must be a single path segment" };
			}
			let cardId: string;
			try {
				cardId = parseCardDocumentId(raw).toIdString();
			} catch {
				return { error: "cardId is invalid" };
			}
			if (cardId.includes("/")) {
				return { error: "cardId must be a single path segment" };
			}
			return { pathname: `/${cardId}`, cardId };
		}
		default: {
			const _exhaustive: never = input.destination;
			return { error: `Unsupported destination: ${_exhaustive}` };
		}
	}
}
